import type { Event, EventHint } from '@sentry/nextjs'

// Pマーク対応: 個人情報の第三者提供（米国 Sentry サーバーへの送信）を防ぐため、beforeSend で PII をマスクする
const PII_PATTERNS: Array<{ pattern: RegExp; replacement: string }> = [
  { pattern: /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g, replacement: '[email]' },
  { pattern: /(?:\+81[-\s]?|0)(?:\d{1,4}[-\s]?)(?:\d{1,4}[-\s]?)\d{3,4}/g, replacement: '[phone]' },
  { pattern: /〒?\d{3}[-ー]\d{4}/g, replacement: '[postal]' },
]

export function maskPii(value: string | null | undefined): string | undefined {
  if (value == null) return undefined
  const withoutPii = PII_PATTERNS.reduce(
    (s, { pattern, replacement }) => s.replace(pattern, replacement),
    value
  )
  // 🔴 URL の秘密もここで落とす。maskPii は message / exception.value / extra /
  // breadcrumb.message / request.data の全ての文字列面が通る唯一の場所なので、
  // ここへ寄せると面ごとの足し忘れが起きない（Codex SO v2 の指摘）。
  return redactSecretsInUrl(withoutPii)
}

// 悪意あるオブジェクトや誤ったネストによるスタックオーバーフローを防ぐため再帰深度を 10 に制限する
export function sanitizeObject(obj: unknown, visited = new Set<object>(), depth = 0): unknown {
  if (depth > 10) return '[truncated]'
  if (obj == null) return obj // null と undefined を一括除外
  if (typeof obj === 'string') return maskPii(obj)
  if (typeof obj !== 'object') return obj // ここ以降 obj は object（null は除外済み）
  if (visited.has(obj)) return '[circular]'
  visited.add(obj)
  if (Array.isArray(obj)) return obj.map((i) => sanitizeObject(i, visited, depth + 1))
  return Object.fromEntries(
    Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
      k,
      sanitizeObject(v, visited, depth + 1),
    ])
  )
}

// ─── URL 中の秘密（入口トークン）の秘匿 ────────────────────────────────────────
// お客様マイページの入口は URL に秘密を載せる。招待は使い切りだが未使用なら期限まで生き、
// 旧ポータルトークンは 1年有効・失効実績0。これらが Sentry（社外）へ残ると、
// Sentry を見られる人がそのまま使えてしまう。
//
// 🔴 **ルートの形で判定する。** 「長い英数字を全部潰す」汎用パターンは採らない。
// 案件ID・顧客IDの UUID まで潰れて障害調査が不能になり、
// 「サニタイズが強すぎて Sentry を使えなくする」という別の失敗になるため。
// 秘密を載せるルートが増えたら tests/quality/sentry-transaction-scrubbing.test.ts が落ちる。
//
// 🔴 **URL 専用の面だけでなく自由文にも当てる。** fetch 失敗の例外文や
// `logError(err, context)` の context にも URL は混ざる。後者は
// packages/shared-errors/src/index.ts:9,24 が **maskPii を通して** Sentry へ送るため、
// maskPii 自体が落とせないと経路ごと素通りする（Codex SO v2 の指摘）。
const REDACTED = '[redacted]'

/** `/customer-portal/<ここ>` が秘密**ではない**と分かっている固定セグメント。 */
const CUSTOMER_PORTAL_LITERAL_SEGMENTS = 'invite|mypage|reset-password'

/** URL・パス・自由文のいずれにも当てられる形。文字列の途中に埋まっていても拾う。 */
const URL_SECRET_PATTERNS: Array<{ pattern: RegExp; replacement: string }> = [
  // /customer-portal/invite/<token>
  {
    pattern: /(\/customer-portal\/invite\/)[^\s/?#"'<>]+/g,
    replacement: `$1${REDACTED}`,
  },
  // /customer-portal/<token>/...（旧トークンURL経路・Task 9 で撤去予定）
  // 固定セグメントは否定先読みで除外する（/customer-portal/mypage/login を壊さない）
  {
    pattern: new RegExp(
      `(\\/customer-portal\\/)(?!(?:${CUSTOMER_PORTAL_LITERAL_SEGMENTS})(?:[/?#"'<>\\s]|$))[^\\s/?#"'<>]+`,
      'g'
    ),
    replacement: `$1${REDACTED}`,
  },
  // クエリ（?token=... / &access_token=... / 生の token=...）
  {
    pattern: /\b(token|access_token|invite_token|reset_token)=[^&\s"'<>]+/g,
    replacement: `$1=${REDACTED}`,
  },
]

/**
 * URL・パス・クエリ文字列・それらを含む自由文から入口トークンを落とす。
 *
 * 正規表現だけで組んでいるのは、URL として解析できない値（例外文・ログの一部）でも
 * 同じ規則を当てるため。パーサを使うと「URL の面」でしか効かず、`logError` の context や
 * 例外文が素通りする。例外を投げる余地も無い（サニタイザが投げると beforeSend ごと落ちて
 * イベントが消える）。
 */
export function redactSecretsInUrl(value: string | undefined | null): string {
  if (!value) return value ?? ''
  return URL_SECRET_PATTERNS.reduce(
    (acc, { pattern, replacement }) => acc.replace(pattern, replacement),
    value
  )
}

/** breadcrumb の data から遷移先URLを秘匿する（navigation / fetch / xhr が URL を持つ）。 */
const BREADCRUMB_URL_KEYS = new Set(['to', 'from', 'url'])
function sanitizeBreadcrumbData(data: Record<string, unknown>): Record<string, unknown> {
  const sanitized = sanitizeObject(data) as Record<string, unknown>
  for (const key of BREADCRUMB_URL_KEYS) {
    const value = sanitized[key]
    if (typeof value === 'string') sanitized[key] = redactSecretsInUrl(value)
  }
  return sanitized
}

/**
 * Sentry へ載せてよいヘッダー名（小文字で比較する・**許可リスト**）。
 *
 * SENTRY-PII-COOKIE-LEAK-1（2026-09-05）:
 * 最初は「落とすものを列挙する」形にしたが、敵対検証で**4本落とし漏らしている**と分かった
 * （x-vercel-oidc-token / x-amz-security-token 等）。秘密ヘッダーは増え続けるので、
 * 列挙方式は増えるたびに漏れる＝ fail-open。**残すものを列挙する**形に変え、
 * 知らないヘッダーは落とす（fail-closed）。
 *
 * ここに無いものが要るようになったら、その都度「載せてよい理由」とともに足す。
 * 障害調査で実際に使うものだけを入れること。
 */
const SAFE_HEADER_NAMES = new Set([
  'user-agent',
  'content-type',
  'content-length',
  'accept',
  'accept-language',
  'accept-encoding',
  'host',
  'referer',
  'origin',
  'x-request-id',
  'x-vercel-id',
  'sentry-trace',
  'baggage',
])

export function sanitizeEventForPii(event: Event, _hint?: EventHint): Event {
  if (event.user) {
    event.user = { id: event.user.id }
  }
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((ex) => ({
      ...ex,
      value: maskPii(ex.value),
    }))
  }
  if (event.message) event.message = maskPii(event.message)
  if (event.extra) event.extra = sanitizeObject(event.extra) as Record<string, unknown>
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => ({
      ...bc,
      message: maskPii(bc.message),
      data: bc.data ? sanitizeBreadcrumbData(bc.data) : bc.data,
    }))
  }
  if (event.request?.data) event.request.data = sanitizeObject(event.request.data)
  if (event.request?.url) event.request.url = redactSecretsInUrl(event.request.url)
  if (event.request?.query_string && typeof event.request.query_string === 'string') {
    event.request.query_string = redactSecretsInUrl(event.request.query_string)
  }
  if (event.transaction) event.transaction = redactSecretsInUrl(event.transaction)
  if (event.request?.headers) {
    // @sentry/core RequestEventData['headers'] の型は { [key: string]: string }（= Record<string, string>）
    const h = event.request.headers as Record<string, string | undefined>
    for (const key of Object.keys(h)) {
      if (!SAFE_HEADER_NAMES.has(key.toLowerCase())) {
        delete h[key]
        continue
      }
      // 許可したヘッダーでも、値に秘密が乗るものは伏せる（referer は招待 URL を運ぶ）
      const value = h[key]
      if (typeof value === 'string') h[key] = redactSecretsInUrl(value)
    }
  }
  // 🔴 headers.cookie を消すだけでは足りない。@sentry/core 10.x は
  // integrations/requestdata.js:133-134 で `requestData.cookies` に**パース済み辞書を別枠で**
  // 載せる（sendDefaultPii:false でも include.cookies は true）。ここを消さないと
  // スタッフのセッショントークンが出続ける（SENTRY-PII-COOKIE-LEAK-1・2026-09-05 実測）。
  if (event.request && 'cookies' in event.request) {
    delete (event.request as { cookies?: unknown }).cookies
  }
  return event
}
